/**
 * Phase 5-B — PulseFloatingPartner.
 *
 * Fixed-position draggable "Pulse Bot" mascot (default bottom-right). The
 * mascot is a two-tier capsule:
 *   • Top tier  — a horizontal "head" with two cyan glowing blinking eyes
 *   • Neck      — a thin vertical connector
 *   • Bottom tier — the hand-crafted "S" badge (PulseAvatar, unchanged)
 *
 * Behaviour (unchanged from the previous orb):
 *   • Click → opens PulseDrawer
 *   • Drag → repositions; distance-based click-vs-drag disambiguation
 *   • Position persisted per user_id (`smartpublish_pulse_pos_<user_id>`)
 *   • Idle breathing (translateY -3 → 0 → -3 over 2.6s) pauses while dragging
 *   • Both eyes share the same blink keyframe; eye-2 has a 60ms stagger so
 *     the mascot still reads as symmetrical (not a wink).
 *
 * Bounding box: Desktop 56×72, Mobile 48×62.
 * Constrained to the visible viewport minus 24px padding + 72px top-safe
 * (nav collision) + 90px bottom offset for first-time default position.
 */

import { motion } from "framer-motion";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import PulseAvatar from "@/components/pulse/PulseAvatar";
import PulseTooltipController from "@/components/pulse/PulseTooltipController";
import { usePulse } from "@/contexts/PulseContext";
import { useSession } from "@/contexts/SessionContext";

const EDGE_PAD = 24;
const TOP_SAFE = 72; // nav height + spacing
const BOTTOM_DEFAULT = 90; // first-time users only; stored positions win
const CLICK_THRESHOLD_PX = 6;
const POS_KEY_PREFIX = "smartpublish_pulse_pos_";

// Mascot dimensions. Height = headH + neck + badge (badge == width).
const DESKTOP_DIMS = { width: 56, height: 72, headW: 38, headH: 14, eye: 3 };
const MOBILE_DIMS = { width: 48, height: 62, headW: 32, headH: 12, eye: 2.5 };

function readStoredPosition(userId) {
    if (!userId) return null;
    try {
        const raw = window.localStorage.getItem(`${POS_KEY_PREFIX}${userId}`);
        if (!raw) return null;
        const p = JSON.parse(raw);
        if (
            p &&
            typeof p.x === "number" &&
            typeof p.y === "number" &&
            Number.isFinite(p.x) &&
            Number.isFinite(p.y)
        ) {
            return p;
        }
    } catch {
        /* ignore */
    }
    return null;
}

function writeStoredPosition(userId, pos) {
    if (!userId) return;
    try {
        window.localStorage.setItem(
            `${POS_KEY_PREFIX}${userId}`,
            JSON.stringify(pos)
        );
    } catch {
        /* ignore */
    }
}

function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

function computeDefaultPosition(dims) {
    if (typeof window === "undefined") {
        return { x: EDGE_PAD, y: EDGE_PAD };
    }
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    return {
        x: vw - dims.width - EDGE_PAD,
        y: vh - dims.height - BOTTOM_DEFAULT,
    };
}

function constrain(pos, dims) {
    if (typeof window === "undefined") return pos;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    return {
        x: clamp(pos.x, EDGE_PAD, Math.max(EDGE_PAD, vw - dims.width - EDGE_PAD)),
        y: clamp(pos.y, TOP_SAFE, Math.max(TOP_SAFE, vh - dims.height - EDGE_PAD)),
    };
}

export default function PulseFloatingPartner() {
    const { user, isAuthenticated } = useSession();
    const { drawerOpen, openDrawer } = usePulse();

    const [dims, setDims] = useState(DESKTOP_DIMS);
    const [pos, setPos] = useState(() => computeDefaultPosition(DESKTOP_DIMS));
    const [hover, setHover] = useState(false);

    // Track drag distance for click-vs-drag detection.
    const dragStartRef = useRef(null);
    const dragDistanceRef = useRef(0);
    const [isDragging, setIsDragging] = useState(false);

    const userId = user?.user_id || null;

    // Pick mascot dimensions based on viewport width.
    useLayoutEffect(() => {
        const update = () => {
            setDims(window.innerWidth < 640 ? MOBILE_DIMS : DESKTOP_DIMS);
        };
        update();
        window.addEventListener("resize", update);
        return () => window.removeEventListener("resize", update);
    }, []);

    // Hydrate position from localStorage once we have a user_id + dims.
    useEffect(() => {
        if (!userId) return;
        const stored = readStoredPosition(userId);
        setPos(constrain(stored || computeDefaultPosition(dims), dims));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, dims]);

    // Re-constrain on viewport resize.
    useEffect(() => {
        const update = () => {
            setPos((p) => {
                const next = constrain(p, dims);
                if (userId && (next.x !== p.x || next.y !== p.y)) {
                    writeStoredPosition(userId, next);
                }
                return next;
            });
        };
        window.addEventListener("resize", update);
        return () => window.removeEventListener("resize", update);
    }, [dims, userId]);

    const handleClick = useCallback(() => {
        if (isDragging) return;
        openDrawer();
    }, [isDragging, openDrawer]);

    const onKeyDown = useCallback(
        (e) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openDrawer();
            }
        },
        [openDrawer]
    );

    if (!isAuthenticated) return null;

    const { width, height, headW, headH, eye: EYE_SIZE } = dims;
    const badgeSize = width;
    const NECK_H = Math.max(height - headH - badgeSize, 0);
    const EYE_GAP = Math.round(headW * 0.22);

    return (
        <>
            <PulseTooltipController orbSize={width} orbPosition={pos} />

            {/* Scoped blink keyframes — ~0.15s closed window inside a 3.5s loop.
                Both eyes use the same animation; eye-right carries a 60ms
                delay so the pair reads as a bot blink, not a wink. */}
            <style>{`
                @keyframes pulse-bot-blink {
                    0%, 92%, 100% { transform: scaleY(1); }
                    94%, 96%       { transform: scaleY(0.08); }
                }
            `}</style>

            <motion.button
                type="button"
                drag
                dragMomentum={false}
                dragElastic={0}
                dragConstraints={{
                    left: EDGE_PAD - pos.x,
                    right:
                        typeof window !== "undefined"
                            ? window.innerWidth - width - EDGE_PAD - pos.x
                            : 0,
                    top: TOP_SAFE - pos.y,
                    bottom:
                        typeof window !== "undefined"
                            ? window.innerHeight - height - EDGE_PAD - pos.y
                            : 0,
                }}
                onDragStart={(_, info) => {
                    dragStartRef.current = {
                        x: info.point.x,
                        y: info.point.y,
                    };
                    dragDistanceRef.current = 0;
                    setIsDragging(true);
                }}
                onDrag={(_, info) => {
                    if (!dragStartRef.current) return;
                    const dx = info.point.x - dragStartRef.current.x;
                    const dy = info.point.y - dragStartRef.current.y;
                    dragDistanceRef.current = Math.sqrt(dx * dx + dy * dy);
                }}
                onDragEnd={(_, info) => {
                    const dist = dragDistanceRef.current;
                    const wasDrag = dist >= CLICK_THRESHOLD_PX;
                    if (wasDrag) {
                        setPos((prev) => {
                            const next = constrain(
                                {
                                    x: prev.x + info.offset.x,
                                    y: prev.y + info.offset.y,
                                },
                                dims
                            );
                            if (userId) writeStoredPosition(userId, next);
                            return next;
                        });
                        // Keep dragging flag long enough to swallow click.
                        setTimeout(() => setIsDragging(false), 120);
                    } else {
                        setIsDragging(false);
                    }
                }}
                onClick={handleClick}
                onKeyDown={onKeyDown}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                aria-label="Open Pulse AI partner"
                data-testid="pulse-orb-trigger"
                data-dragging={isDragging ? "true" : "false"}
                data-drawer-open={drawerOpen ? "true" : "false"}
                style={{
                    position: "fixed",
                    left: pos.x,
                    top: pos.y,
                    width,
                    height,
                    zIndex: 50,
                    touchAction: "none",
                    cursor: isDragging ? "grabbing" : "pointer",
                    background: "transparent",
                    border: "none",
                    padding: 0,
                }}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
            >
                {hover && !isDragging && !drawerOpen && (
                    <motion.span
                        initial={{ opacity: 0, x: 6 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="pointer-events-none absolute right-full top-1/2 mr-3 -translate-y-1/2 whitespace-nowrap rounded-md border border-[#6366F1]/40 bg-[#0B1220] px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-[#E8ECF5] shadow-[0_8px_24px_rgba(0,0,0,0.5)]"
                        data-testid="pulse-orb-tooltip"
                    >
                        Pulse · Your AI Partner
                    </motion.span>
                )}

                {/* Breathing wrapper — pauses while dragging. */}
                <motion.div
                    animate={isDragging ? { y: 0 } : { y: [-3, 0, -3] }}
                    transition={
                        isDragging
                            ? { duration: 0.2, ease: "easeOut" }
                            : {
                                  duration: 2.6,
                                  repeat: Infinity,
                                  ease: "easeInOut",
                              }
                    }
                    style={{
                        position: "relative",
                        width: "100%",
                        height: "100%",
                        pointerEvents: "none",
                    }}
                    data-testid="pulse-bot-mascot"
                >
                    {/* Head tier */}
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: headW,
                            height: headH,
                            backgroundColor: "#0B1220",
                            borderRadius: headH,
                            border: "1px solid rgba(14,165,233,0.55)",
                            boxShadow:
                                "0 0 10px rgba(14,165,233,0.35), inset 0 0 0 1px rgba(255,255,255,0.04)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: EYE_GAP,
                        }}
                        data-testid="pulse-bot-head"
                    >
                        {/* Each eye = outer motion.span (hover-scale) wrapping
                            an inner span that owns the blink keyframe. We
                            split the two so framer-motion's `transform:scale`
                            does NOT clobber the CSS keyframe `scaleY` blink. */}
                        <motion.span
                            aria-hidden
                            animate={{ scale: hover && !isDragging ? 1.15 : 1 }}
                            transition={{
                                type: "spring",
                                stiffness: 320,
                                damping: 20,
                            }}
                            style={{
                                width: EYE_SIZE,
                                height: EYE_SIZE,
                                display: "inline-block",
                            }}
                            data-testid="pulse-bot-eye-left"
                        >
                            <span
                                style={{
                                    display: "block",
                                    width: "100%",
                                    height: "100%",
                                    borderRadius: "50%",
                                    backgroundColor: "#00F5D4",
                                    boxShadow:
                                        "0 0 4px rgba(0,245,212,0.9), 0 0 8px rgba(14,165,233,0.55)",
                                    animation:
                                        "pulse-bot-blink 3.5s ease-in-out infinite",
                                    animationDelay: "0ms",
                                    transformOrigin: "center",
                                }}
                            />
                        </motion.span>
                        <motion.span
                            aria-hidden
                            animate={{ scale: hover && !isDragging ? 1.15 : 1 }}
                            transition={{
                                type: "spring",
                                stiffness: 320,
                                damping: 20,
                            }}
                            style={{
                                width: EYE_SIZE,
                                height: EYE_SIZE,
                                display: "inline-block",
                            }}
                            data-testid="pulse-bot-eye-right"
                        >
                            <span
                                style={{
                                    display: "block",
                                    width: "100%",
                                    height: "100%",
                                    borderRadius: "50%",
                                    backgroundColor: "#00F5D4",
                                    boxShadow:
                                        "0 0 4px rgba(0,245,212,0.9), 0 0 8px rgba(14,165,233,0.55)",
                                    animation:
                                        "pulse-bot-blink 3.5s ease-in-out infinite",
                                    animationDelay: "60ms",
                                    transformOrigin: "center",
                                }}
                            />
                        </motion.span>
                    </div>

                    {/* Neck connector */}
                    <div
                        aria-hidden
                        style={{
                            position: "absolute",
                            top: headH,
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: 2,
                            height: NECK_H,
                            background:
                                "linear-gradient(to bottom, rgba(14,165,233,0.6), rgba(99,102,241,0.5))",
                            borderRadius: 1,
                        }}
                    />

                    {/* Badge tier — existing hand-crafted "S" medallion. */}
                    <div
                        style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            width: badgeSize,
                            height: badgeSize,
                        }}
                    >
                        <PulseAvatar
                            size={badgeSize}
                            showPulse={!drawerOpen && !isDragging}
                            glowIntensity={hover ? 1.3 : 1}
                            data-testid="pulse-orb-avatar"
                        />
                    </div>
                </motion.div>
            </motion.button>
        </>
    );
}
